import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CookieBanner = () => {
  const [show, setShow] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show banner after a short delay
      setTimeout(() => setShow(true), 1000);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(cookieConsent);
        setPreferences(saved);
      } catch (e) {
        console.error('Error loading cookie preferences:', e);
      }
    }
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    setShow(false);
    // Here you would typically initialize/disable analytics based on preferences
    if (prefs.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      functional: true
    };
    savePreferences(allAccepted);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      functional: false
    };
    savePreferences(essentialOnly);
  };

  const handleSaveCustom = () => {
    savePreferences(preferences);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up" data-testid="cookie-banner">
      <div className="bg-white border-t-2 border-gray-200 shadow-2xl">
        <div className="container mx-auto px-4 py-6">
          {!showCustomize ? (
            // Main Banner
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-green-100 to-cyan-100 rounded-full flex items-center justify-center">
                  <Cookie className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Cookie &amp; data consent</h3>
                  <p className="text-sm text-gray-600">
                    We use essential cookies so the booking engine and PayPal checkout work correctly &mdash; these cannot be switched off. Analytics and functional cookies are optional and help us improve the site.{' '}
                    <Link to="/privacy" className="text-green-600 hover:underline font-medium">
                      Privacy policy
                    </Link>{' '}
                    &middot;{' '}
                    <Link to="/cookies" className="text-green-600 hover:underline font-medium">
                      Cookie policy
                    </Link>
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 w-full md:w-auto">
                <Button
                  onClick={handleAcceptAll}
                  className="text-white"
                  style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
                  data-testid="cookie-accept-all"
                >
                  Accept All
                </Button>
                <Button
                  onClick={handleRejectNonEssential}
                  variant="outline"
                  className="border-gray-300"
                  data-testid="cookie-reject"
                >
                  Reject Non-Essential
                </Button>
                <Button
                  onClick={() => setShowCustomize(true)}
                  variant="ghost"
                  className="text-gray-600"
                  data-testid="cookie-customize"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Customize
                </Button>
              </div>
            </div>
          ) : (
            // Customization Panel
            <div data-testid="cookie-customize-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Cookie Preferences</h3>
                <button
                  onClick={() => setShowCustomize(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="essential"
                    checked={preferences.essential}
                    disabled
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="essential" className="font-semibold text-gray-900 block">
                      Essential Cookies (Required)
                    </label>
                    <p className="text-sm text-gray-600">
                      Required for core site functions including the booking engine, PayPal checkout, security and session integrity. These cannot be disabled.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="analytics"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="mt-1"
                    data-testid="cookie-analytics-toggle"
                  />
                  <div className="flex-1">
                    <label htmlFor="analytics" className="font-semibold text-gray-900 block cursor-pointer">
                      Analytics Cookies
                    </label>
                    <p className="text-sm text-gray-600">
                      Help us understand how visitors use our website so we can improve our services.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="functional"
                    checked={preferences.functional}
                    onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                    className="mt-1"
                    data-testid="cookie-functional-toggle"
                  />
                  <div className="flex-1">
                    <label htmlFor="functional" className="font-semibold text-gray-900 block cursor-pointer">
                      Functional Cookies
                    </label>
                    <p className="text-sm text-gray-600">
                      Remember your preferences and provide enhanced, personalized features.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleSaveCustom}
                  className="text-white"
                  style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
                  data-testid="cookie-save-preferences"
                >
                  Save Preferences
                </Button>
                <Button
                  onClick={() => setShowCustomize(false)}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
